import _ from 'lodash';
import BaseGallery from '../ui/BaseGallery';
import mixin from '../privates/utils/mixin';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import {validate} from '../privates/validations/validation';
import {GALLERY_CLICK_ACTIONS} from '../privates/constants';
import reporter from '../privates/reporter';
import * as members from '../privates/privateMembers';
import imageUtils from '../privates/utils/imageUtils';
import platformUtils from '../privates/platformUtils';

const PRO_GALLERY_APP_DEF_ID = '14271d6f-ba62-d045-549b-ab972ae1f70e';

function getPubSub(contextId) {
    return platformUtils.getPubSubService().getBoundedAPI(contextId, PRO_GALLERY_APP_DEF_ID);
}

function initGalleryPubSub(contextId, compId, RMI) {
    const pubSubService = getPubSub(contextId);
    pubSubService.unsubscribe(`galleryAction_${compId}`);
    pubSubService.subscribe(`galleryAction_${compId}`, ({data}) => {
        if (_.get(data, 'action') === 'onItemClicked') {
            const callbackIds = RMI.getEventCallbackIds(compId, RMI.getEventTypes().ITEM_CLICKED);
            _.forEach(callbackIds, (callbackId) => {
                const dynamicCallback = RMI.getCallbackById(callbackId, true);
                const staticCallback = platformUtils.getStaticEventHandler(callbackId);
                const eventHandler = dynamicCallback || staticCallback;
                const callback = _.get(eventHandler, 'callback');
                if (_.isFunction(callback)) {
                    callback(_.get(data, 'payload.event'));
                }
            });
        }
    });
}

/* REMOVING ALL JSDOC FOR THIS CLASS AND ITS APIs (see https://jira.wixpress.com/browse/WCD-3056) */
class Gallery extends BaseGallery { // This is named Gallery on purpose so that the user sees it as Gallery
    constructor(RMI, compId, contextId, wixSelector, controllerId, role, type, emitter){
        super(RMI, compId, contextId, wixSelector, controllerId, role, type, emitter);
        initGalleryPubSub(contextId, compId, RMI);
    }

    /* REMOVING JSDOC FROM THIS API (see https://jira.wixpress.com/browse/WCD-3056) */
    get type() {
        return '$w.Gallery';
    }

    /* REMOVING JSDOC FROM THIS API (see https://jira.wixpress.com/browse/WCD-3056) */
    set items(items) {
        const imageItems = imageUtils.filterImageItems(items);
        const areAllImagesValid = _.every(imageItems,
            (imageData, imageIndex) => imageUtils.checkImageValidation({imageData, imageIndex, compName: this.id}));
        if (!areAllImagesValid) {
            return;
        }
        const newItems = _.map(items, (item, index) => {
            if (_.includes(imageItems, item)) {
                return imageUtils.dataFormat.toModel(item, index);
            }
            return item;
        });

        if(!_.every(newItems)){
            return;
        }

        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const contextId = members._contextId.get(this);
        RMI.setData(compId, {items: newItems});
        getPubSub(contextId).publish(`galleryAction_${compId}`, {action: 'setItems', payload: {items: newItems}}, true);
    }

    get items() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const data = RMI.getData(compId);
        if (!data.items) {
            reporter.reportSdkWarning('retrieving items from a Pro Gallery is only supported after the items have been set via code');
            return null;
        }
        const items = data.items || [];
        const imageItems = imageUtils.filterImageItems(items);
        return _.map(items, (item, index) => {
            if (_.includes(imageItems, item)) {
                return imageUtils.dataFormat.toUser(item, index);
            }
            return item;
        });
    }

    // The following block of JSDoc must be included due to jsdoc-events lint rule:
    /**
     * @function onItemClicked
     * @param {$w.GalleryItemClickedEventHandler} handler
     * @returns {$w.Gallery}
     * @see [clickAction](#clickAction)
     * @memberof $w.Gallery
     * @eventtype itemClicked
     * @eventhandler
     * @ignore
     */
    onItemClicked(cb) {
        const compType = members._type.get(this).compType;
        if (validate({
            compType,
            functionName: 'onItemClicked',
            dataToValidate: {onItemClicked: cb},
            compName: this.id
        })) {
            const RMI = members._RMI.get(this);
            const contextId = members._contextId.get(this);
            const controllerId = members._controllerId.get(this);
            const compId = members._compId.get(this);
            RMI.registerEvent(contextId, compId, RMI.getEventTypes().ITEM_CLICKED, controllerId, cb);
        }
        return this;
    }

    /* REMOVING JSDOC FROM THIS API (see https://jira.wixpress.com/browse/WCD-3056) */
    get galleryCapabilities() {
        return Object.freeze({
            isPlayable: false,
            hasCurrentItem: false,
            hasNavigationButtons: false,
            supportsAllMediaTypes: true
        });
    }

    get clickAction() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const galleryImageClickAction = _.get(RMI.getProps(compId), 'galleryImageOnClickAction');
        if (!galleryImageClickAction) {
            reporter.reportSdkWarning('retrieving clickAction from a Pro Gallery is only supported after the items have been set via code');
            return null;
        }
        return _.findKey(GALLERY_CLICK_ACTIONS, (v) => v === galleryImageClickAction);
    }

    set clickAction(action) {
        const compType = members._type.get(this).compType;
        if (validate({
            compType,
            functionName: 'clickAction',
            dataToValidate: {galleryImageOnClickAction: action},
            compName: this.id
        })) {
            const galleryImageClickAction = _.isNil(action) ? GALLERY_CLICK_ACTIONS.none : GALLERY_CLICK_ACTIONS[action];
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);
            const contextId = members._contextId.get(this);
            RMI.setProps(compId, {galleryImageOnClickAction: galleryImageClickAction});
            getPubSub(contextId).publish(`galleryAction_${compId}`, {action: 'setClickAction', payload: {action: galleryImageClickAction}}, true);
        }
    }

    get options() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const galleryOptions = _.get(RMI.getProps(compId), 'galleryOptions');
        if (!galleryOptions) {
            reporter.reportSdkWarning('retrieving galleryOptions from a Pro Gallery is only supported after the options have been set via code');
            return null;
        }
        return {...galleryOptions};
    }

    set options(galleryOptions) {
        const compType = members._type.get(this).compType;
        if (validate({
            compType,
            functionName: 'options',
            dataToValidate: {galleryOptions},
            compName: this.id
        })) {
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);
            const contextId = members._contextId.get(this);
            RMI.setProps(compId, {galleryOptions});
            getPubSub(contextId).publish(`galleryAction_${compId}`, {action: 'setStyleParams', payload: {...galleryOptions}}, true);
        }
    }
}

mixin(Gallery.prototype, HiddenCollapsedMixin.prototype);

export default Gallery;
